import { formatFairPayCompactAmount, formatFairPayVersion } from "@components/utils/fairPayFormat";

/**
 * Version bar type enum (ER-11556).
 *
 * The effective-dated records rules rename `past` → `lapsed`. The old `past` and
 * `passed` keys are still accepted by `normalizeVersionBarState` so call sites
 * that have not been migrated keep rendering instead of silently falling back
 * to `lapsed`.
 */
export const VERSION_BAR_STATE = {
  LAPSED: "lapsed",
  CURRENT: "current",
  FUTURE: "future",
  DRAFT: "draft",
};

const VALID_STATES = new Set(Object.values(VERSION_BAR_STATE));
const MIDDLE_DOT = "\u00B7";
const EN_DASH = "\u2013";

export function normalizeVersionBarState(raw) {
  const key = String(raw || "")
    .toLowerCase()
    .trim();
  if (key === "passed" || key === "past") return VERSION_BAR_STATE.LAPSED;
  return VALID_STATES.has(key) ? key : VERSION_BAR_STATE.LAPSED;
}

/** Bar and row amount with an uppercase K / M: `$96K`. */
export function formatVersionBarAmount(value) {
  return formatVersionBarRange(value, "$");
}

/**
 * Bar range amount, e.g. `$96K`.
 * Prefix is configurable so non-USD pay ranges can use their currency code.
 */
export function formatVersionBarRange(value, currencySymbol = "$") {
  if (value == null || value === "") return "";
  if (typeof value === "string") {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return `${currencySymbol}${value}`;
    value = numeric;
  }
  return formatFairPayCompactAmount(value, currencySymbol);
}

/**
 * Bar label — pattern `V{n} · {min}–{max}`.
 * Left-aligned by the component; truncates with an ellipsis (full string on hover).
 */
export function formatVersionBarLabel(versionNumber, amountMin, amountMax, currencySymbol = "$") {
  const version = formatFairPayVersion(versionNumber);
  const min = formatVersionBarRange(amountMin, currencySymbol);
  const max = formatVersionBarRange(amountMax, currencySymbol);
  if (!min && !max) return version;
  if (!max) return `${version} ${MIDDLE_DOT} ${min}`;
  if (!min) return `${version} ${MIDDLE_DOT} ${max}`;
  return `${version} ${MIDDLE_DOT} ${min}${EN_DASH}${max}`;
}

export function formatVersionBarAriaLabel(
  state,
  versionNumber,
  amountMin,
  amountMax,
  currencySymbol = "$",
  selected = false
) {
  const stateLabel = normalizeVersionBarState(state);
  const min = formatVersionBarRange(amountMin, currencySymbol);
  const max = formatVersionBarRange(amountMax, currencySymbol);
  const base =
    min && max
      ? `${stateLabel} pay range version ${versionNumber}, ${min} to ${max}`
      : `${stateLabel} pay range version ${versionNumber}`;
  return selected ? `${base}, selected` : base;
}
